// src/components/ui/dropdown-menu.js
import React, { useState, useRef, createContext, useContext } from "react";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";

const DropdownMenuContext = createContext(null);

export function DropdownMenu({ children }) {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen }}>
      {children}
    </DropdownMenuContext.Provider>
  );
}

export function DropdownMenuTrigger({ children, asChild }) {
  const { open, setOpen } = useContext(DropdownMenuContext);

  const handleClick = (e) => {
    e.preventDefault();
    setOpen(!open);
  };

  if (asChild) {
    return React.cloneElement(children, {
      onClick: handleClick,
      "aria-expanded": open,
    });
  }

  return (
    <button onClick={handleClick} aria-expanded={open}>
      {children}
    </button>
  );
}

export function DropdownMenuContent({
  children,
  align = "center",
  className,
  ...props
}) {
  const { open, setOpen } = useContext(DropdownMenuContext);
  const ref = useRef(null);

  useOnClickOutside(ref, () => setOpen(false));

  if (!open) return null;

  const alignClasses = {
    start: "left-0",
    center: "left-1/2 -translate-x-1/2",
    end: "right-0",
  };

  return (
    <div
      ref={ref}
      className={`absolute z-50 mt-2 min-w-[8rem] overflow-hidden rounded-md border bg-white p-1 shadow-md animate-in data-[side=bottom]:slide-in-from-top-2 ${
        alignClasses[align]
      } ${className || ""}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function DropdownMenuItem({ children, className, ...props }) {
  const { setOpen } = useContext(DropdownMenuContext);

  const handleClick = (e) => {
    if (props.onClick) {
      props.onClick(e);
    }
    setOpen(false);
  };

  return (
    <button
      className={`relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 ${
        className || ""
      }`}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

export function DropdownMenuLabel({ children, className, ...props }) {
  return (
    <div
      className={`px-2 py-1.5 text-sm font-semibold ${className || ""}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function DropdownMenuSeparator({ className, ...props }) {
  return (
    <div
      className={`-mx-1 my-1 h-px bg-gray-200 ${className || ""}`}
      {...props}
    />
  );
}
